/*
 * STEP 3 — Video Clip Upload
 * Design: Quiet Reverence
 * Supports MP4, MOV, HEVC from phone cameras.
 * Shows duration and file size. Caption per clip.
 */

import { ACCEPTED_VIDEO_TYPES, VideoAsset, formatFileSize } from "./intake";
import { Film, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
const nanoid = (n = 10) => Math.random().toString(36).substring(2, 2 + n);

interface Props {
  videos: VideoAsset[];
  onChange: (videos: VideoAsset[]) => void;
}

const MAX_VIDEOS = 5;

export default function Step3Videos({ videos, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    const remaining = MAX_VIDEOS - videos.length;
    const toAdd = arr.slice(0, remaining);
    const newVideos: VideoAsset[] = toAdd.map(file => ({
      id: nanoid(),
      file,
      previewUrl: URL.createObjectURL(file),
      caption: '',
    }));
    onChange([...videos, ...newVideos]);
  }, [videos, onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const removeVideo = (id: string) => {
    const v = videos.find(v => v.id === id);
    if (v) URL.revokeObjectURL(v.previewUrl);
    onChange(videos.filter(v => v.id !== id));
  };

  const updateCaption = (id: string, caption: string) => {
    onChange(videos.map(v => v.id === id ? { ...v, caption } : v));
  };

  return (
    <div className="step-enter space-y-6">
      <p className="text-sm text-muted-foreground">
        Add up to <strong className="text-foreground">{MAX_VIDEOS} video clips</strong> — home movies, celebrations, candid moments.
        These will be woven into the tribute alongside the photos.
      </p>

      {/* Upload Zone */}
      {videos.length < MAX_VIDEOS && (
        <div
          className={`upload-frame p-8 flex flex-col items-center justify-center gap-3 cursor-pointer ${dragOver ? 'drag-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <Film className="w-7 h-7 text-primary" />
          </div>
          <div className="text-center">
            <p className="font-['Playfair_Display'] font-600 text-foreground text-lg">Add Video Clips</p>
            <p className="text-sm text-muted-foreground mt-1">Tap to browse or drag videos here</p>
            <p className="text-xs text-muted-foreground mt-1">MP4, MOV, HEVC — any size</p>
          </div>
          <input
            ref={inputRef}
            id="video-file-input"
            name="video_files"
            type="file"
            accept={ACCEPTED_VIDEO_TYPES}
            multiple
            className="hidden"
            onChange={e => e.target.files && addFiles(e.target.files)}
          />
        </div>
      )}

      {/* Video List */}
      {videos.length > 0 && (
        <div className="space-y-4">
          {videos.map((video) => (
            <div key={video.id} className="thumb-enter flex gap-3 p-3 bg-card rounded-xl border border-border">
              {/* Thumbnail */}
              <div className="relative flex-shrink-0">
                <video
                  src={video.previewUrl}
                  className="w-20 h-20 object-cover rounded-lg bg-muted"
                  muted
                  playsInline
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center">
                    <Film className="w-4 h-4 text-white" />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeVideo(video.id)}
                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-sm hover:opacity-90 transition"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              {/* Caption */}
              <div className="flex-1 space-y-1.5">
                <div className="text-xs text-muted-foreground truncate">{video.file ? `${video.file.name} · ${formatFileSize(video.file.size)}` : video.r2Key ? '✓ Uploaded to cloud' : 'File ready'}</div>
                <input
                  id={`video-caption-${video.id}`}
                  name={`video_caption_${video.id}`}
                  type="text"
                  value={video.caption}
                  onChange={e => updateCaption(video.id, e.target.value)}
                  placeholder='e.g. "Family vacation, Lake Tahoe 1972"'
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {videos.length === 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground font-['Playfair_Display'] italic">
          Video clips are optional — even a single clip brings a tribute to life.
        </div>
      )}

      <div className="p-4 rounded-xl bg-muted/50 border border-border">
        <p className="text-xs text-muted-foreground">
          <strong className="text-foreground">Tip:</strong> Short clips of 15–60 seconds work best.
          Our team will trim and blend them with your photos.
          Phone videos upload exactly as taken — no editing needed.
        </p>
      </div>
    </div>
  );
}
